import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  changeChatMessagingState,
  setChatReadToggleState,
  toggleBlockUser,
  toggleChatFlow,
  changeCustomerAgent
} from '../../../../actions/inbox/conversationActions';

/**
 * @fileoverview Hook the Actions Layer for management of actions of the header in the chat window of the Inbox.
 * Provides optimized handlers for business operations on the conversation: change of agent, blocking, chat state, flow and reading.
 * Follows the architecture of strict separation of layers and optimization of performance (useCallback, memoization).
 * @module useHeaderChatWindowActions
 * @category Hook - Actions Layer
 * @since 3.1.0
 * @version 3.1.0
 */

export default function useHeaderChatWindowActions(conversation) {
  const dispatch = useDispatch();
  const { t } = useTranslation('InboxMC');
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser || {});
  const canAssignChatsToAgents = Boolean(currentRetailerUser?.can_assign_chats_to_agents);
  const canToggleCustomerBlock = Boolean(currentRetailerUser?.can_toggle_customer_block);
  const currentRetailerUserId = Number(currentRetailerUser?.id);

  /**
   * Changes the messaging state of the chat (active, closed, etc).
   *
   * @function handleMessagingState
   * @memberof useHeaderChatWindowActions
   * @param {string} messagingStateOption - New messaging state
   * @returns {void}
   * @example
   * handleMessagingState('active');
   */
  const handleMessagingState = useCallback((messagingStateOption) => {
    if (Number(messagingStateOption) !== Number(conversation?.statusChat)) {
      const body = [{
        id: conversation?.id,
        platform: conversation?.platform,
        status_chat: messagingStateOption
      }];
      const options = [{
        id: conversation?.id,
        status_chat: conversation?.statusChat
      }];
      dispatch(changeChatMessagingState(body, options));
    }
  }, [conversation, dispatch]);

  /**
   * Assigns a new agent to the conversation after confirmation.
   *
   * @function handleChangeAgent
   * @memberof useHeaderChatWindowActions
   * @param {string} agentValue - ID of the new agent to assign
   * @returns {void}
   * @example
   * handleChangeAgent('agent_123');
   */
  const handleChangeAgent = useCallback((agentValue) => {
    if (!canAssignChatsToAgents && Number(agentValue) !== currentRetailerUserId) return;

    if (Number(agentValue) !== Number(conversation?.assignedAgent?.id) && confirm(t('header.confirm_assign_agent'))) {
      const newAgent = { id: agentValue };
      const body = [{ id: conversation?.id, platform: conversation?.platform, newAgent }];
      const options = [{ id: conversation?.id, prevAgent: conversation?.assignedAgent }];

      dispatch(changeCustomerAgent(body, options));
    }
  }, [canAssignChatsToAgents, conversation, currentRetailerUserId, dispatch, t]);

  /**
   * Blocks or unblocks the user after confirmation.
   *
   * @function handleToggleBlockUser
   * @memberof useHeaderChatWindowActions
   * @returns {void}
   * @example
   * handleToggleBlockUser();
   */
  const handleToggleBlockUser = useCallback(() => {
    if (!canToggleCustomerBlock) return;

    const confirmMsg = conversation?.userIsBlocked
      ? t('header.confirm_unblock_user')
      : t('header.confirm_block_user');
    if (confirm(confirmMsg)) {
      dispatch(toggleBlockUser(conversation?.id, {
        id: conversation?.customerId
      }, conversation?.userIsBlocked));
    }
  }, [canToggleCustomerBlock, dispatch, t, conversation]);

  /**
   * Activates or deactivates the chat flow for the conversation.
   *
   * @function handleToggleChatFlow
   * @memberof useHeaderChatWindowActions
   * @returns {void}
   * @example
   * handleToggleChatFlow();
   */
  const handleToggleChatFlow = useCallback(() => {
    dispatch(toggleChatFlow(conversation?.id, {
      id: conversation?.customerId,
      chat_service: conversation?.platform
    }, conversation?.hasAllowFlow, conversation?.hasFlow));
  }, [conversation, dispatch]);

  /**
   * Marks the conversation as read.
   *
   * @function handleUnreadChat
   * @memberof useHeaderChatWindowActions
   * @returns {void}
   * @example
   * handleUnreadChat();
   */
  const handleUnreadChat = useCallback(() => {
    dispatch(setChatReadToggleState([{
      id: conversation.id,
      platform: conversation.platform
    }], { unreadState: true }));
  }, [conversation, dispatch]);

  /**
   * Return of the hook with all the header action handlers.
   * @returns {Object} Handlers of actions
   */
  return {
    handleMessagingState,
    handleChangeAgent,
    handleToggleBlockUser,
    handleToggleChatFlow,
    handleUnreadChat
  };
}
